import { ReactElement } from 'react';
import Item from '@tencent/dui/lib/components/Menu/Item';
import SubMenu from '@tencent/dui/lib/components/Menu/SubMenu';
import { STSchemeColorVal } from '@tencent/slide-sdk-pb-model/src/ultrabuf/ultrabuf.types';
import { ITDColor, TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';

// 暴露插件返回的函数
export interface IColorPickerExtension extends IExtension {
    getRender: () => React.ComponentType<IShowOptions> | undefined;
    show: (options?: IShowOptions & { container?: HTMLDivElement | null }) => void;
    setRecentColors(color: ISingleColorUnit): void;
}

/**
 * 指定色盘的类型, 从上至下依次为
 *  高亮行列色盘
 *  高亮块色盘
 *  突出显示色盘
 *  pc 端大色盘
 *  移动端大色盘
 *  标签色盘
 *  自定义色盘
 * 具体参考需求: http://tapd.woa.com/TIM_web/prong/stories/view/1010143861873337235
 */
export enum ColorPickerType {
    HightlightRowCol = 'highlightRowCol',
    HighlightBackground = 'highlightBackground',
    HighlightText = 'highlightText',
    StandardPC = 'standardPC',
    StandardMobile = 'standardMobile',
    Tag = 'tag',
    Custom = 'custom',
}

export enum ColorPickerItemType {
    Color = 'color',
    Custom = 'custom',
    Menu = 'menu',
    MenuDivider = 'menuDivider',
}

// 色块组件，包括主题色、标准色、标签色、最近使用和其他自定义颜色
export enum ColorStyleType {
    EditPalette = 'editpalette',
    TagPalette = 'tagpalette',
    HighlightPalette = 'hightlightpalette',
    BlockPalette = 'blockpalette',
    Recent = 'recent',
    ThemeWithLum = 'themeWidthLum', // 带有 lum 信息的主题色盘（ooxml 数据结构）
}

// 颜色来源类型, 配合原本的数据上报
export enum ColorSourceType {
    Recent = 'recent',
    Custom = 'custom',
    Theme = 'theme',
    Standard = 'standard',
    Eyedropper = 'eyedropper',
}

// 自定义上传的色盘内组件类型
export type IItemList = (
    IColorPickerColor
    | IColorPickerCustom
    | IColorPickerMenu
    | IColorPickerMenuDivider
)[];


// 调用方调用show时传入的参数
export interface IShowOptions {
    colorPickerType?: ColorPickerType;
    style?: React.CSSProperties;
    rowSpan?: number;
    size?: number;
    actions?: (() => any)[] | IColorPickerAction[];
    selectedColor?: string;
    /**
     * 用户自定义的主题色盘中，色值不是唯一键
     * 该参数只有 ThemeWithLum 有效
     */
    selectedToken?: string | string[];
    eyedropperDisabled?: boolean;
    eyedroppperContainer?: HTMLElement;
    itemList?: IItemList;
    onColorChange?: (color: string, source: ColorSourceType, options?) => void;
    onEyedropperVisibleChange?: (visible: boolean) => void;
    shouldPreventDefault?: boolean;
    onColorHover?: (color?: string, source?: ColorSourceType) => void; // 鼠标悬浮在色盘的色块上
    onMouseEnterGroup?: (source?: ColorSourceType) => void; // 鼠标进入 group 事件
    onMouseLeaveGroup?: (source?: ColorSourceType) => void; // 鼠标离开 group 事件（取消预览）
}

// 操作组件, eg. 无颜色操作按钮, 无边框操作按钮 etc.
export interface IColorPickerAction {
    text: string;
    position?: 'top' | 'bottom';
    onClick?: () => any;
}

export interface ISingleColorUnit {
    color?: string;
    tip?: string;
    time?: number;
}

export type IHoverEffectType = 'enlarge' | 'bound';

export interface IColorPickerColor {
    itemType: ColorPickerItemType.Color;
    payload?: {
        colorItemClassName?: string;
        styleName?: Exclude<ColorStyleType, ColorStyleType.ThemeWithLum>;
        colors?: ISingleColorUnit[];
        theme?: string;
        hoverEffect?: IHoverEffectType;
        label?: string;
        labelSize?: number;
        text?: string;
        toolTipDisabled?: boolean;
        maxShowNum?: number; // 仅针对最近颜色设置
    } | {
        colorItemClassName?: string;
        styleName: ColorStyleType.ThemeWithLum,
        themeItems: IThemeItem[];
        lumItems: ILumItem[][];
        hoverEffect?: IHoverEffectType;
        overrideTheme?: boolean;
        label?: string;
        labelSize?: number;
        theme?: string;
    };
}

// 自定义颜色
export interface IColorPickerCustom {
    itemType: ColorPickerItemType.Custom;
    payload?: {
        label?: string;
        eyedropperDisabled?: boolean;
        eyedroppperContainer?: HTMLElement;
        onEyedropperVisibleChange?: (visible: boolean) => any;
    };
}

// 更多的菜单部分
export interface IColorPickerMenu {
    itemType: ColorPickerItemType.Menu;
    payload?: {
        menus?: ReactElement<Item | SubMenu>;
    };
}

// 菜单的分割线
export interface IColorPickerMenuDivider {
    itemType: ColorPickerItemType.MenuDivider;
    payload?: {};
}

export interface ILumItem {
    lumMod?: number;
    lumOff?: number;
    lumModTips?: string;
    lumOffTips?: string;
}

export interface IThemeItem {
    val: STSchemeColorVal;
    color?: string | null;
    tips?: string;
}

/**
 * 选中额外的参数
 * 因为用户自定义主题色需要返回 themeKey
 */
export interface IColorSelectExtraOptions {
    type: ColorStyleType.ThemeWithLum;
    theme?: IThemeItem,
    lum?: ILumItem,
    token: string;
}

export enum ColorPickerEntry {
    ANIM =  'anim_sidebar'
}

/**
 * 主题色色块
 */
export const THEME_ITEM_KEY = 'theme';
export interface ISlideShowOptions {
    colorPickerType?: ColorPickerType;
    style?: React.CSSProperties;
    rowSpan?: number;
    size?: number;
    actions?: (() => any)[] | IColorPickerAction[];
    entry?: ColorPickerEntry;
    // selectedColor?: string;
    /**
     * 用户自定义的主题色盘中，色值不是唯一键
     * 该参数只有 ThemeWithLum 有效
     */
    // selectedToken?: string | string[];
    selectedTdColor?: ITDColor | 'multi',
    selectedGradientColor?: string,
    eyedropperDisabled?: boolean;
    eyedroppperContainer?: HTMLElement;
    itemList?: IItemList;
    onEyedropperVisibleChange?: (visible: boolean) => void;
    shouldPreventDefault?: boolean;
    onColorHover?: (color?: string, source?: ColorSourceType) => void; // 鼠标悬浮在色盘的色块上
    onMouseEnterGroup?: (source?: ColorSourceType) => void; // 鼠标进入 group 事件
    onMouseLeaveGroup?: (source?: ColorSourceType) => void; // 鼠标离开 group 事件（取消预览）
    disabledCustomEyeDropper?: boolean;
    themePanelLabel?: string;
    isStandardColorPicker?: boolean;
    // 主题、自定义色回调
    onColorChange?(color: TDColor, source: ColorSourceType): void;
    // 渐变色回调
    onGradualColorChange?(color: string, source: ColorSourceType): void;
    onMouseLeave?(): void;
    onClick?(): void;
}


// 色盘操作框位置
export enum ColorPickerActionType {
    Top = 'top',
    Bottom = 'bottom'
}

export interface IReportInfo {
    reportPoint: EReportPoint;
    actionType: string;
}

export enum EReportPoint {
    TOOLBAR = 'toolbar',
    TITLEBAR = 'titlebar',
    DRAWER = 'drawer',
    FLOAT = 'float',
}
